import React, { useState, useContext, useEffect } from "react";
import { Box,CircularProgress,FormControl,FormControlLabel,Grid,IconButton, Radio, RadioGroup, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { TrialContainer } from "./styles.ts";
import { PrimaryButton } from "../studioxr/Button.tsx";
import { fetchBilling } from "./billingRefusal";
import { useLoginState } from "../studioxr/auth/LoginState.ts";
import { ProfileInterface, UserProfileRole } from "ts-interfaces";
import { useMattoState } from "../MattoState.ts";
import { cancelStripeSubscriptionAPIUrl, getSubscriptionAPIUrl, getStripePitchImage } from "./config.js";

import { CancellationSurvey } from "./CancellationSurvey.tsx";
import { PauseOffer, PauseOption } from "./PauseMembership.tsx";
import * as Sentry from "@sentry/react";

// Smaller than the trial modal's 25px title: this column is half a 640px modal.
// "&&&" outranks TrialContainer's ".main-title" rule.
const titleSx = { '&&&': { fontSize: '20px' } };

const CancelUserSubscription = ({setShowSubscription, pausedCancel = null}) => {

  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const setProfile = useLoginState((state) => state.setProfile);
  const [cancelButtonDetails, setCancelButtonDetails] = useState({label:"Continue Cancellation", disable:false});
  const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
  // The live subscription decides; the profile stamp can lag the webhook.
  const paused = pausedCancel !== null || profile?.billingState === 'paused';


  async function cancelStripeSubscription() {
    const requestCancellation = { customerId: profile?.stripeCustomerId };
    return fetchBilling("cancelStripeSubscription", requestCancellation, cancelStripeSubscriptionAPIUrl);
  }
  const cancelSubscription = async() => {   
    try {
      // Only the uid: the whole profile (email, name, Stripe ids) has no place
      // in an info breadcrumb (issue #3956 F).
      Sentry.captureMessage("User attempted to cancel subscription", {
        level: "info",
        user: { id: profile?.uid },
      });

      if(profile?.stripeSubscriptionId){ 
        setCancelButtonDetails({label: "Cancelling... Please Wait!", disable: true})
        const cancelStripeSubscriptionData  = await cancelStripeSubscription()
  
        if (
          cancelStripeSubscriptionData?.subscription?.status == "canceled" ||
          cancelStripeSubscriptionData?.notice?.message=='Customer does not have an active subscription') {
            const dataToUpdate = {
              userRole: UserProfileRole.BASIC,
              trialends: null,
              // A past_due sub cancels immediately; the dunning banner must
              // go with it (the webhook clears the fields too). Same for a
              // paused sub cancelled after a skipped charge (#3957).
              billingState: null,
              billingNextAttemptAt: null,
              billingResumesAt: null,
            }
            const updatedProfile = {...profile, ...dataToUpdate}
            setProfile(updatedProfile);
            setSubscriptionCanceled(true);
        }
        // An active sub is cancelled at period end, so it comes back still
        // "active" — cancel_at_period_end is the only success signal here.
        else if (cancelStripeSubscriptionData?.subscription?.cancel_at_period_end) {
          // Cancelling a paused sub inside its paid period lifts the pause
          // (#3957): Pro is back until it ends (the webhook writes the same).
          if (paused) {
            setProfile({...profile, userRole: UserProfileRole.PRO, billingState: null, billingResumesAt: null});
          }
          setSubscriptionCanceled(true);
        }
        // fetchBilling returns undefined on failure, so anything else
        // is a failed cancellation — never show the success screen for it.
        else {
          throw new Error(
            cancelStripeSubscriptionData?.error?.message || "Cancellation was not confirmed"
          );
        }
      }

    } catch (error) {
      setCancelButtonDetails({label: "Continue Cancellation", disable: false});
      Sentry.captureException(error);
      alert("There is some error in cancelling your subscription. Please contact studio@mattoboard.com for assistance.")
    }
  };

    return (
      // `safe center`, not `center`: plain centering puts overflow above
      // scrollTop 0, where it can never be scrolled to on short viewports.
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'safe center', height: '100%', padding: '15px', overflowY: 'auto' }}>
        {!subscriptionCanceled &&
        <>
          <Typography className="main-title" sx={{paddingBottom:'10px', ...titleSx}} >
            We are sorry to see you go.  Please click below to confirm cancel. </Typography>
          {pausedCancel === 'period_end' &&
            <Typography className="para-sub">Pro comes back until your next billing date, then your membership ends. You won't be charged again.</Typography>}
          {pausedCancel === 'now' &&
            <Typography className="para-sub">Your paused membership ends as soon as you confirm.</Typography>}
          <PrimaryButton
            fullWidth
            variant="contained"
            onClick={cancelSubscription}
            disabled={cancelButtonDetails.disable}
            sx={{
              mt: 2,
              padding: '8px',
              fontWeight: 400,
              fontSize: '16px',
              textTransform: 'capitalize',
              borderRadius: '8px',
              background: '#000',
              color: '#fff',
              '&:hover': { background: '#222' },
            }}
          >
            {cancelButtonDetails.label}
          </PrimaryButton>
        </>
        }
        {subscriptionCanceled &&
        <>
          <Typography className="main-title" sx={{paddingBottom:'10px', ...titleSx}} >
            Subscription canceled successfully. You will not be charged for any future billings. </Typography>
          <CancellationSurvey
            uid={profile?.uid}
            email={profile?.email}
            stripeCustomerId={profile?.stripeCustomerId}
          />
          <PrimaryButton
            fullWidth
            variant="contained"
            onClick={() => { setShowSubscription(null);}}
            sx={{
              mt: 2,
              padding: '8px',
              fontWeight: 400,
              fontSize: '16px',
              textTransform: 'capitalize',
              borderRadius: '8px',
              background: '#000',
              color: '#fff',
              '&:hover': { background: '#222' },
            }}
          >
            Continue Designing!
          </PrimaryButton>
        </>
        }
      </Box>
    );
  };


export const CancelSubscription = (props) => {

  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  // Pause before cancel (issue #3957). null while the live subscription
  // loads; empty when it cannot pause, which leaves the plain cancel screen.
  const [pauseOptions, setPauseOptions] = useState<PauseOption[] | null>(null);
  const [periodStart, setPeriodStart] = useState<number | null>(null);
  const [continueToCancel, setContinueToCancel] = useState(false);
  // How a paused member's cancel ends: with Pro back until the paid period
  // ends, or at once after a skipped charge. null when not paused.
  const [pausedCancel, setPausedCancel] = useState<'period_end' | 'now' | null>(null);

  useEffect(() => {
    let active = true;
    if (!profile?.stripeCustomerId) { setPauseOptions([]); return; }
    // fetchBilling returns undefined on failure: cancelling is never blocked.
    fetchBilling("getSubscription", { customerId: profile.stripeCustomerId }, getSubscriptionAPIUrl).then((data) => {
      if (!active) return;
      if (data?.subscription?.pause_collection) setPausedCancel(data.pausedCancelEndsNow ? 'now' : 'period_end');
      const start = data?.subscription?.current_period_start ?? data?.subscription?.items?.data?.[0]?.current_period_start;
      setPeriodStart(typeof start === 'number' ? start : null);
      setPauseOptions(Array.isArray(data?.pauseOptions) ? data.pauseOptions : []);
    });
    return () => { active = false; };
  }, [profile?.stripeCustomerId]);

  const offerPause = !continueToCancel && pauseOptions !== null && pauseOptions.length > 0;
  // The cancel confirmation keeps its pitch image, also when reached through
  // "Continue to cancel". Only the pause offer is single column, as in the mockup.
  const showPitchImage = pauseOptions !== null && !offerPause;

    return (
      <TrialContainer>
        {/* A narrower column than the trial modal's: this modal stays the pause offer's size. */}
        {showPitchImage &&
          <img
            src={getStripePitchImage('width=600,format=auto', 2)}
            alt=""
            className="trial-graphic"
            aria-hidden="true"
            style={{ borderRadius: 0, objectFit: 'cover', height: '100%', flex: '0 0 50%' }}
          />}
        <IconButton sx={{zIndex:'100'}} onClick={() => setShowSubscription(null)} className="close-btn" aria-label="Close">
          <CloseIcon />
        </IconButton>
        {pauseOptions === null && !continueToCancel ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <CircularProgress size={28} sx={{ color: '#8C8C8C' }} />
          </Box>
        ) : offerPause ? (
          <PauseOffer
            options={pauseOptions}
            periodStart={periodStart}
            onContinueToCancel={() => setContinueToCancel(true)}
            onClose={() => setShowSubscription(null)}
          />
        ) : (
          <Box className="form-root trial">
            <CancelUserSubscription setShowSubscription={setShowSubscription} pausedCancel={pausedCancel} />
          </Box>
        )}
      </TrialContainer>
    );
  };
  